<template>
  <div class="invoice-values pa-4 overflow--y" :style="`height: ${contentHeight}px`">
    <div class="financial-drawer__total--line">
      <v-layout>
        <v-flex xs6>
          <span class="text--nowrap">{{ $t(`${namespace}.totalPayable`) }}</span>
        </v-flex>
        <v-flex class="text-xs-right" xs6>
          <strong class="featured-value">{{ total | currencyBRL }}</strong>
        </v-flex>
      </v-layout>
    </div>

    <hr class="invoice-divider">

    <v-layout class="mb-2">
      <v-flex class="legend text-uppercase" xs6>
        {{ $t(`${namespace}.service`) }}
      </v-flex>
      <v-flex class="legend text-xs-right" xs6>
        {{ $t('Common.currency') }}
      </v-flex>
    </v-layout>

    <v-layout v-if="ridesRawAmount">
      <v-flex xs6>
        <span class="text--nowrap">{{ $t(`${namespace}.rides`) }}</span>
      </v-flex>
      <v-flex class="text-xs-right" xs6>
        <span class="text--semibold">{{ convertToCurrencyBRL(ridesRawAmount) }}</span>
      </v-flex>
    </v-layout>

    <v-layout v-if="saasMonthlyAmount">
      <v-flex xs6>
        <span class="text--nowrap">{{ $t(`${namespace}.fixedServiceFee`) }}</span>
      </v-flex>
      <v-flex class="text-xs-right" xs6>
        <span class="text--semibold">{{ convertToCurrencyBRL(saasMonthlyAmount) }}</span>
      </v-flex>
    </v-layout>

    <v-layout v-if="saasRideAmount">
      <v-flex xs6>
        <span class="text--nowrap">{{ $t(`${namespace}.serviceFeePerRide`) }}</span>
      </v-flex>
      <v-flex class="text-xs-right" xs6>
        <span class="text--semibold">{{ convertToCurrencyBRL(saasRideAmount) }}</span>
      </v-flex>
    </v-layout>

    <v-layout
      v-if="franchiseValue || franchiseAmount"
      class="success--text"
    >
      <v-flex xs6>
        <span class="text--nowrap">{{ $t(`${namespace}.franchise`) }} ({{ franchiseValue }} {{ $t(`${namespace}.rides`).toLowerCase() }})</span>
      </v-flex>

      <v-flex class="success--text text-xs-right" xs6>
        <span class="text--semibold">(-) {{ convertToCurrencyBRL(franchiseAmount) }}</span>
      </v-flex>
    </v-layout>

    <v-layout v-if="chargeBack" class="success--text">
      <v-flex xs6>
        <span class="text--nowrap">{{ $t(`${namespace}.chargeback`) }}</span>
      </v-flex>
      <v-flex class="success--text text-xs-right" xs6>
        <span class="text--semibold">(-) {{ convertToCurrencyBRL(chargeBack) }}</span>
      </v-flex>
    </v-layout>

    <div v-if="contractedDiscountValue || contractedDiscountAmount || data.credit">
      <hr class="invoice-divider">

      <v-layout v-if="subtotalServices">
        <v-flex xs6>
          <span class="text--nowrap">{{ $t(`${namespace}.subtotal`) }}</span>
        </v-flex>
        <v-flex class="text-xs-right" xs6>
          <span class="text--semibold">{{ convertToCurrencyBRL(subtotalServices) }}</span>
        </v-flex>
      </v-layout>

      <v-layout
        v-if="contractedDiscountValue || contractedDiscountAmount"
        class="success--text"
      >
        <v-flex xs6>
          <span class="text--nowrap">
            {{ $t(`${namespace}.contractedDiscount`) }} ({{ contractedDiscountValue }}%)
          </span>
        </v-flex>
        <v-flex class="text-xs-right" xs6>
          <span class="text--semibold">(-) {{ convertToCurrencyBRL(contractedDiscountAmount) }}</span>
        </v-flex>
      </v-layout>

      <v-layout v-if="data.credit" class="success--text">
        <v-flex xs6>
          <span class="text--nowrap">{{ $t(`${namespace}.credit`) }}</span>
        </v-flex>
        <v-flex class="text-xs-right" xs6>
          <span class="text--semibold">(-) {{ convertToCurrencyBRL(data.credit) }}</span>
        </v-flex>
      </v-layout>
    </div>

    <div v-if="data.fine || data.defaultInterest">
      <hr class="invoice-divider">

      <v-layout v-if="data.total">
        <v-flex xs6>
          <span class="text--nowrap">{{ $t(`${namespace}.subtotal`) }}</span>
        </v-flex>
        <v-flex class="text-xs-right" xs6>
          <span class="text--semibold">{{ convertToCurrencyBRL(data.total) }}</span>
        </v-flex>
      </v-layout>

      <v-layout v-if="data.fine" class="error--text">
        <v-flex xs6>
          <span class="text--nowrap">{{ $t(`${namespace}.trafficTicket`) }}</span>
        </v-flex>
        <v-flex class="text-xs-right" xs6>
          <span class="text--semibold">(+) {{ convertToCurrencyBRL(data.fine) }}</span>
        </v-flex>
      </v-layout>

      <v-layout v-if="data.defaultInterest" class="error--text">
        <v-flex xs6>
          <span class="text--nowrap">{{ $t(`${namespace}.interestOnLatePayments`) }}</span>
        </v-flex>
        <v-flex class="text-xs-right" xs6>
          <span class="text--semibold">(+) {{ convertToCurrencyBRL(data.defaultInterest) }}</span>
        </v-flex>
      </v-layout>
    </div>

    <hr class="invoice-divider">

    <div class="financial-drawer__total--line">
      <v-layout>
        <v-flex xs6>
          <span class="text--nowrap">{{ $t('Common.totalPayable') }}</span>
        </v-flex>
        <v-flex class="text-xs-right" xs6>
          <strong class="featured-value">{{ total | currencyBRL }}</strong>
        </v-flex>
      </v-layout>
    </div>
  </div>
</template>

<script>
import { isArray, calcContentHeight, convertToCurrencyBRL } from 'utils/functions'

export default {
  name: 'InvoiceValues',
  props: {
    type: {
      type: String
    },
    data: {
      type: Object,
      default: () => {}
    }
  },
  computed: {
    ridesRawAmount () {
      return this.data.rides && this.data.rides.rawAmount
        ? this.data.rides.rawAmount
        : 0
    },

    saasMonthlyAmount () {
      return this.data.saasMonthly && this.data.saasMonthly.amount
        ? this.data.saasMonthly.amount
        : 0
    },

    saasRideAmount () {
      return this.data.saasRide && this.data.saasRide.amount
        ? this.data.saasRide.amount
        : 0
    },

    franchise () {
      const discounts = this.data.rides && this.data.rides.discounts && isArray(this.data.rides.discounts)
        ? this.data.rides.discounts
        : []

      const franchise = discounts.find(discount => discount.type === 'SAAS_BY_RIDE')
      return franchise
    },

    franchiseValue () {
      return this.franchise && this.franchise.value ? this.franchise.value : 0
    },

    franchiseAmount () {
      return this.franchise && this.franchise.amount ? this.franchise.amount : 0
    },

    chargeBack () {
      const ridesChargeBack = this.data.rides && this.data.rides.chargeBack
        ? this.data.rides.chargeBack
        : 0
      const saasRideChargeBack = this.data.saasRide && this.data.saasRide.chargeBack
        ? this.data.saasRide.chargeBack
        : 0
      const saasMonthlyChargeBack = this.data.saasMonthly && this.data.saasMonthly.chargeBack
        ? this.data.saasMonthly.chargeBack
        : 0

      return ridesChargeBack + saasRideChargeBack + saasMonthlyChargeBack
    },

    contractedDiscount () {
      const discounts = this.data.rides && this.data.rides.discounts && isArray(this.data.rides.discounts)
        ? this.data.rides.discounts
        : []

      const contractedDiscount = discounts.find(discount => discount.type === 'PERCENT')
      return contractedDiscount
    },

    contractedDiscountValue () {
      return this.contractedDiscount && this.contractedDiscount.value ? this.contractedDiscount.value : 0
    },

    contractedDiscountAmount () {
      return this.contractedDiscount && this.contractedDiscount.amount ? this.contractedDiscount.amount : 0
    },

    subtotalServices () {
      return this.ridesRawAmount + this.saasMonthlyAmount + this.saasRideAmount - this.franchiseValue - this.chargeBack
    },

    total () {
      return this.data.totalValueWithInterests || this.data.total
    }
  },
  data () {
    return {
      namespace: 'Components.GlobalDrawer.financial.invoices',
      contentHeight: 'none'
    }
  },
  methods: {
    convertToCurrencyBRL (val) {
      return val ? convertToCurrencyBRL(val).replace('R$ ', '') : 0
    },

    calcContentHeight () {
      this.contentHeight = calcContentHeight() - (65 + 32)
    }
  },
  mounted () {
    this.calcContentHeight()
    window.addEventListener('resize', this.calcContentHeight)
  },
  beforeDestroy () {
    window.addEventListener('resize', this.calcContentHeight)
  }
}
</script>

<style lang="scss" scoped>
.invoice-values {
  background-color: $gray-1;
}

.invoice-divider {
  margin-top: 16px;
  margin-bottom: 16px;
  opacity: 0.3
}

.financial-drawer__total--line {

  .value {
    font-weight: 600;
    text-align: right;
  }

  &:last-child {

    .value {
      font-size: 16px;
    }
  }

  &.has-border {
    border-bottom: 1px solid $gray-4;
    margin-bottom: 12px;
    padding-bottom: 12px;
  }
}

.legend {
  font-size: 10px;
}

.featured-value {
  font-size: 16px;
}

.debug1 {
  outline: 1px solid red;
}

.debug2 {
  outline: 1px solid blue;
}
</style>
